import type {
  CreateProductTypeRequest,
  ProductType,
  ProductTypeAttributeAssignment,
  UpdateProductTypeRequest,
} from '@nucleus-commerce/api-client/admin';

import type {
  ProductTypeFormInput,
  ProductTypeFormValues,
} from '../schemas/product-type-form-schema';

/** Lowercase letters, digits and hyphens: the backend's slug shape. "Éternité Band" → "eternite-band". */
export function slugFrom(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function emptyProductTypeForm(): ProductTypeFormInput {
  return {
    name: '',
    slug: '',
    description: '',
    skuCode: '',
    position: '0',
    attributes: [],
    defaultCustomizationIds: [],
    suggestedFaq: [],
  };
}

export function fromProductType(row: ProductType): ProductTypeFormInput {
  return {
    name: row.name,
    slug: row.slug,
    description: row.description ?? '',
    skuCode: row.skuCode ?? '',
    position: String(row.position),
    attributes: row.attributes
      .toSorted((a, b) => a.position - b.position)
      .map((a) => ({ attributeId: a.attributeId, isRequired: a.isRequired })),
    defaultCustomizationIds: [...row.defaultCustomizationIds],
    suggestedFaq: row.suggestedFaq.map(({ question, answer }) => ({ question, answer })),
  };
}

/**
 * The list is sent whole and replaces the stored one, so order becomes position. The backend
 * clears a `defaultValue` that is not sent; an assignment that already had one gets it back.
 */
function toAssignments(
  values: ProductTypeFormValues,
  existing: ProductType | null,
): ProductTypeAttributeAssignment[] {
  const defaults = new Map(
    (existing?.attributes ?? []).flatMap((a) =>
      a.defaultValue === null || a.defaultValue === undefined
        ? []
        : [[a.attributeId, a.defaultValue] as const],
    ),
  );
  return values.attributes.map((a, position) => {
    const assignment: ProductTypeAttributeAssignment = {
      attributeId: a.attributeId,
      isRequired: a.isRequired,
      position,
    };
    if (defaults.has(a.attributeId)) assignment.defaultValue = defaults.get(a.attributeId);
    return assignment;
  });
}

export function toProductTypeCreate(values: ProductTypeFormValues): CreateProductTypeRequest {
  return {
    name: values.name,
    slug: values.slug,
    description: values.description || null,
    skuCode: values.skuCode || null,
    position: Number(values.position),
    defaultCustomizationIds: values.defaultCustomizationIds,
    suggestedFaq: values.suggestedFaq,
    attributes: toAssignments(values, null),
  };
}

export function toProductTypeUpdate(
  values: ProductTypeFormValues,
  existing: ProductType,
): UpdateProductTypeRequest {
  return {
    name: values.name,
    slug: values.slug,
    description: values.description || null,
    skuCode: values.skuCode || null,
    position: Number(values.position),
    defaultCustomizationIds: values.defaultCustomizationIds,
    suggestedFaq: values.suggestedFaq,
    attributes: toAssignments(values, existing),
  };
}
